import type { ReactNode } from 'react'

import type { StatsigGateId } from '~/configs/statsig'

/**
 * Generic, surface-agnostic types for the customizable grid framework. Both the
 * dashboard (`src/app/dashboard/v2`) and reports (`src/app/reports/v2`) build on
 * these — a "surface" supplies its own widget registry, templates, persistence
 * hooks and page-chrome filter bar through `CustomizeContext`. The type is named
 * `DashboardLayout` for historical reasons; read it as "a dashboard-style grid
 * layout" — it is not coupled to the dashboard surface.
 */

export type SlotSize = 'full' | 'half' | 'third' | 'quarter'

export type RowLayout = 'full' | 'halves' | 'thirds' | 'quarters'

export const SLOT_COUNT_BY_LAYOUT: Record<RowLayout, number> = {
  full: 1,
  halves: 2,
  thirds: 3,
  quarters: 4
}

export const SLOT_SIZE_BY_LAYOUT: Record<RowLayout, SlotSize> = {
  full: 'full',
  halves: 'half',
  thirds: 'third',
  quarters: 'quarter'
}

export const ROW_LAYOUTS: RowLayout[] = ['full', 'halves', 'thirds', 'quarters']

/**
 * Width ranking for slot sizes. Used to compare a slot's size against a
 * widget's declared `minSize` (a widget fits when `SLOT_SIZE_RANK[slotSize]
 * >= SLOT_SIZE_RANK[minSize]`). Bigger rank = wider slot.
 */
export const SLOT_SIZE_RANK: Record<SlotSize, number> = {
  quarter: 0,
  third: 1,
  half: 2,
  full: 3
}

export function isSlotAtLeast(slotSize: SlotSize, minSize: SlotSize | undefined): boolean {
  if (!minSize) return true
  return SLOT_SIZE_RANK[slotSize] >= SLOT_SIZE_RANK[minSize]
}

/**
 * Widget IDs are intentionally typed as `string` rather than a closed union so
 * each surface can register its own widget types without modifying this file.
 * Templates and call sites should reference exported `*_WIDGET_ID` constants
 * from a surface's registry for type-safe references to known widgets.
 */
export type WidgetId = string

export type WidgetInstance = {
  instanceId: string
  widgetId: WidgetId
  settings?: unknown
}

export type Slot = {
  slotId: string
  widget: WidgetInstance | null
}

export type Row = {
  rowId: string
  layout: RowLayout
  slots: Slot[]
}

export type DashboardLayout = {
  id: string
  /** Template id this layout was created from (used for "reset to template"). */
  basedOn?: string
  /**
   * User-facing name. Optional and surface-specific: the reports surface uses it
   * for its named "saved reports" switcher. Surfaces that don't name layouts
   * (the dashboard) simply leave it unset.
   */
  name?: string
  rows: Row[]
}

export type WidgetCategory = 'status' | 'live' | 'charts' | 'lists' | 'billing' | 'map' | 'controls'

/**
 * The set of page-level filters a widget's data source supports. `'site'`
 * widgets accept organization + site filters; `'org'` widgets accept
 * organization only (e.g. vehicle metrics, which the backend does not filter by
 * site). A surface that mixes scopes (reports) keeps each layout homogeneous so
 * the page-chrome filter bar always matches what every tile can honor.
 */
export type FilterScope = 'site' | 'org'

export type WidgetRenderProps = {
  /**
   * Per-instance settings persisted alongside the slot. Each widget owns its
   * own settings shape — the framework treats this as opaque `unknown` and
   * widgets are responsible for parsing/defaulting their own values. Mutate
   * via the surface's `setWidgetSettings(slotId, next)`.
   */
  settings?: unknown
  /**
   * Slot id this widget is mounted in. Required when the widget mutates its
   * own `settings` so the dispatch can target the correct slot. Stable across
   * renders of the same instance.
   */
  slotId: string
  size: SlotSize
}

export type WidgetDefinition = {
  id: WidgetId
  category: WidgetCategory
  /**
   * Minimum slot size this widget renders well at. Widgets are allowed in any
   * slot whose size is at least this wide. Omit for widgets that fit any size.
   */
  minSize?: SlotSize
  /**
   * When true, users may place multiple instances on the same layout (e.g.
   * low SoC with different thresholds). Defaults to false.
   */
  allowMultiple?: boolean
  /**
   * The page-level filter scope this widget supports. Surfaces that mix scopes
   * (reports) use this to keep a layout homogeneous and to drive which filters
   * the page chrome shows. Omit on single-scope surfaces (the dashboard).
   */
  filterScope?: FilterScope
  Render: (props: WidgetRenderProps) => ReactNode
  requiredAbility?: { action: string; subject: string }
  /**
   * Statsig gate that must pass for this widget to appear in the picker and
   * render in a saved layout. Omit for ungated widgets (the common case).
   */
  requiredGate?: StatsigGateId
}

/**
 * Subset of layout-mutation callbacks the shared framework components depend
 * on. A surface produces this object from its own persistence hooks and passes
 * it through `CustomizeContext`. Keeping it as plain callbacks (resolved once
 * by the surface provider) avoids calling hooks indirectly inside leaf
 * components.
 */
export type CustomizeMutations = {
  moveOrSwapWidget: (fromSlotId: string, toSlotId: string) => void
  removeWidgetFromSlot: (slotId: string) => void
  resetLayout: () => void
  setWidgetInSlot: (slotId: string, widgetId: string) => void
  applyTemplate: (templateId: string) => void
  previewChangeRowLayout: (
    rowId: string,
    nextLayout: RowLayout
  ) => { displaced: WidgetInstance[]; newSlotIds: string[] } | null
  changeRowLayout: (rowId: string, nextLayout: RowLayout, newSlotIds?: string[]) => void
  addRow: (afterRowId: string | null, newRowLayout: RowLayout) => void
  removeRow: (rowId: string) => void
  setWidgetSettings: (slotId: string, settings: unknown) => void
}
